import { Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { apiClient } from '@/lib/api-client';
import { useCurrentIdentity } from '@/lib/identity-context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { extractErrorMessage } from './harness-error';

const DEFAULT_HARNESS_URL =
  typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:5173'
    : 'https://trackercopharness.hillsdale.dev';

type ConsentMode = 'no-consent' | 'accept-all' | 'reject-all';

export function HarnessIndexPage() {
  const { hasPermission } = useCurrentIdentity();
  const canRun = hasPermission('harness:run');

  if (!canRun) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Harness scan</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            You don&rsquo;t have permission to run harness scans
            (<code>harness:run</code>). Ask a tenant owner to grant access.
          </p>
        </CardContent>
      </Card>
    );
  }

  return <HarnessRunner />;
}

function HarnessRunner() {
  const queryClient = useQueryClient();
  const [harnessBaseUrl, setHarnessBaseUrl] = useState(DEFAULT_HARNESS_URL);
  const [consentMode, setConsentMode] = useState<ConsentMode>('accept-all');

  const startMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await apiClient.POST('/harness-scans', {
        body: { harnessBaseUrl, consentMode },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['harness'] });
      // Navigate to the coverage page lazily — the user can refresh to
      // poll progress; SSE wiring is a follow-up.
      if (data?.crawlId) {
        window.history.pushState(null, '', `/harness/${data.crawlId}`);
        window.dispatchEvent(new Event('popstate'));
      }
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Run a harness scan</CardTitle>
          <p className="text-xs text-muted-foreground">
            Targets the GTM exercise harness, exercises every scenario in
            <code> /tracker-scenarios.json</code>, and produces a
            coverage report against the manifest&rsquo;s
            <code> expectedTrackerFamilies</code>.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[2fr,1fr,auto]">
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-muted-foreground">Harness URL</span>
              <Input
                value={harnessBaseUrl}
                onChange={(e) => setHarnessBaseUrl(e.target.value)}
                placeholder="https://trackercopharness.hillsdale.dev"
                spellCheck={false}
              />
              <span className="text-[11px] text-muted-foreground">
                Allowlist: <code>localhost:5173</code>,{' '}
                <code>127.0.0.1:5173</code>,{' '}
                <code>trackercopharness.hillsdale.dev</code>
              </span>
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="text-muted-foreground">Consent mode</span>
              <select
                className="h-9 rounded-md border bg-background px-3 text-sm"
                value={consentMode}
                onChange={(e) => setConsentMode(e.target.value as ConsentMode)}
              >
                <option value="accept-all">accept-all</option>
                <option value="reject-all">reject-all</option>
                <option value="no-consent">no-consent</option>
              </select>
            </label>
            <div className="flex items-end">
              <Button
                onClick={() => startMutation.mutate()}
                disabled={startMutation.isPending || !harnessBaseUrl}
              >
                {startMutation.isPending ? 'Starting…' : 'Start scan'}
              </Button>
            </div>
          </div>
          {startMutation.isError && (
            <div
              className="rounded border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
              data-testid="harness-start-error"
            >
              {extractErrorMessage(startMutation.error)}
            </div>
          )}
          {startMutation.isSuccess && startMutation.data && (
            <div className="rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
              Started crawl <code>{startMutation.data.crawlId}</code> with{' '}
              {startMutation.data.scenarioCount} scenarios.{' '}
              <Link
                to="/harness/$crawlId"
                params={{ crawlId: startMutation.data.crawlId }}
                className="underline underline-offset-2"
              >
                Open coverage report
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
